import { useEffect, useState } from "./vendor.js";


// ============================================================
// ROLE DEFINITIONS — Super-Admin / Inhaber / Mitarbeiter
// ============================================================
export const ROLES = {
  admin: {
    label: "Super-Admin",
    color: "#DC2626",
    description: "Shearly Plattform-Administrator",
  },
  owner: {
    label: "Inhaber",
    color: "#7C3AED",
    description: "Salon-Inhaber / Geschäftsführer",
  },
  employee: {
    label: "Mitarbeiter",
    color: "#3B82F6",
    description: "Salon-Mitarbeiter",
  },
  // TAX_CONSULTANT_KASSE_v1 (08.09.2026) -- fehlte: Sidebar liest ROLES[role].label -> Absturz
  // "Cannot read properties of undefined (reading 'label')" beim ersten Steuerberater-Login.
  receptionist: {
    label: "Rezeption",
    color: "#0EA5E9",
    description: "Empfang / Rezeption",
  },
  tax_consultant: {
    label: "Steuerberatung",
    color: "#10B981",
    description: "Steuerberater/in (nur lesend)",
  },
};

// Which pages each role can see
export const ROLE_PAGES = {
  admin: ["admin-dashboard", "admin-salons", "admin-onboarding", "admin-monitor", "admin-costs", "admin-pricing", "admin-audit", "admin-frontend-errors", "admin-updates", "admin-subscriptions", "settings", "profile", "datenschutz", "agb", "avv", "impressum"],
  owner: ["dashboard", "calendar", "bookings", "customers", "services", "preise", "employees", "ai-calls", "messages", "online-booking", "shifts", "time-tracking", "payments", "kasse", "vouchers", "accounting", "google", "subscription", "settings", "dsgvo", "profile", "help", "datenschutz", "agb", "avv", "impressum"], /* POS_SHEARLY_SSO_v1: +kasse */
  employee: ["dashboard", "calendar", "bookings", "customers", "shifts", "time-tracking", "kasse", "vouchers", "google", "profile", "help"], /* POS_SHEARLY_SSO_v1: +kasse */
  /* TAX_CONSULTANT_v1 -- Steuerberater sieht NUR Buchhaltung + sein Profil + Datenschutz */
  tax_consultant: ["tax-dashboard", "profile", "datenschutz", "impressum"],
};

// ============================================================
// DATA LAYER — Mock data & state helpers
// ============================================================

// COLOR_THEME_v1 -- Lila/Gold-Switch. COLORS.primary etc. als HEX (damit `${COLORS.primary}10`-Tints funktionieren).
export const _COLOR_THEMES_v1 = {
  purple:  { primary: "#7C3AED", primaryDark: "#5B21B6", primaryLight: "#A78BFA" },
  gold:    { primary: "#D4AF6E", primaryDark: "#B89358", primaryLight: "#E8C593" },
  schwarz: { primary: "#1F2937", primaryDark: "#111827", primaryLight: "#4B5563" },  // COLOR_THEME_v2: Anthrazit/Schwarz
  grau:    { primary: "#6B7280", primaryDark: "#4B5563", primaryLight: "#9CA3AF" },  // COLOR_THEME_v2: Neutral-Grau
};
export const _COLOR_THEME_KEYS_v1 = ["purple", "gold", "schwarz", "grau"];
export const _activeColorTheme_v1 = (() => {
  try { const v = localStorage.getItem("shearly_color_theme"); return _COLOR_THEME_KEYS_v1.includes(v) ? v : "purple"; }
  catch { return "purple"; }
})();
// CSS-Vars synchron setzen (für Stellen die direkt var(--primary) nutzen)
try {
  const _t = _COLOR_THEMES_v1[_activeColorTheme_v1];
  document.documentElement.style.setProperty("--primary", _t.primary);
  document.documentElement.style.setProperty("--primaryDark", _t.primaryDark);
  document.documentElement.style.setProperty("--primaryLight", _t.primaryLight);
  document.documentElement.setAttribute("data-color-theme", _activeColorTheme_v1);
} catch(e) {}

export const COLORS = {
  // COLOR_THEME_v1: HEX-Werte aus aktivem Theme (damit Hex-Suffix-Tints `${COLORS.primary}10` funktionieren)
  primary: _COLOR_THEMES_v1[_activeColorTheme_v1].primary,
  primaryLight: _COLOR_THEMES_v1[_activeColorTheme_v1].primaryLight,
  primaryDark: _COLOR_THEMES_v1[_activeColorTheme_v1].primaryDark,
  accent: "var(--accent)",
  success: "var(--success)",
  danger: "var(--danger)",
  warning: "var(--warning)",
  info: "var(--info)",
  bg: "var(--bg)",
  card: "var(--card)",
  text: "var(--text)",
  textLight: "var(--textLight)",
  border: "var(--border)",
};
// COLOR_THEME_v1 -- expose COLORS für LoginScreen-Lila-Override
try { window.__SHEARLY_OUTER_COLORS__ = COLORS; } catch(e) {}

// ============================================================
// MOBILE DETECTION
// ============================================================
export const MOBILE_BP = 768;
export const useIsMobile = () => {
  const [m, setM] = useState(window.innerWidth < MOBILE_BP);
  useEffect(() => {
    const h = () => setM(window.innerWidth < MOBILE_BP);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return m;
};
export const MobileMenuContext = React.createContext(null);
export const NavigateContext = React.createContext(null);
export const DemoModeContext = React.createContext(false);
// SPOTLIGHT_v1.2 -- Context damit der Header-Lupen-Button Spotlight öffnen kann
export const SpotlightContext = React.createContext(null);
// HEADER_AVATAR_v1 -- currentUser via Context (statt hardcoded "AD" im Header)
export const UserContext = React.createContext(null);
export const getUserInitials = (name) => {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const ini = parts.map(w => w[0]).join("").substring(0, 2).toUpperCase();
  return ini || "?";
};

export const HAIR_TYPES = ["glatt", "wellig", "lockig", "kraus"];
export const HAIR_LENGTHS = ["kurz", "mittellang", "lang", "sehr lang"];
export const HAIR_THICKNESS = ["dünn/fein", "normal", "dick/kräftig"];
export const HAIR_CONDITIONS = ["gesund", "trocken", "fettig", "strapaziert", "coloriert", "chemisch behandelt"];
export let CAL_START_HOUR = 8;
export let CAL_END_HOUR = 20;
/* BUSINESS_TYPE_v1 -- Branche des Salons (friseur|kosmetik), kommt via /api/features mit.
   Kosmetik blendet Friseur-Elemente aus: Haarlängen-Feld im Buchungsmodal, "Stylist"-Labels.
   Rollback = DB-Wert auf 'friseur' (oder NULL) setzen → exakt altes Verhalten, ohne Deploy. */
export let SALON_BUSINESS_TYPE = "friseur";
export const isKosmetikSalon = () => SALON_BUSINESS_TYPE === "kosmetik";
// INDEX_SPLIT_B1_v1: Setter, weil ein importiertes `let` in anderen Modulen nicht zugewiesen werden darf
export const setSalonBusinessType = (v) => { SALON_BUSINESS_TYPE = v; };
/* BUSINESS_TYPE_v2 -- Kategorien-Standardliste je Branche (Dienstleistungs-Anlage, Kategorie-Dropdowns) */
export const KOSMETIK_CATEGORIES = ["Gesicht", "Körper", "Hände & Nägel", "Füße", "Wimpern & Brauen", "Haarentfernung", "Sonstiges"];
export const FRISEUR_CATEGORIES = ["Damen", "Herren", "Kinder", "Schnitt", "Farbe", "Pflege", "Styling", "Sonstiges"];
export const defaultCategories = () => isKosmetikSalon() ? KOSMETIK_CATEGORIES : FRISEUR_CATEGORIES;
export const generateTimeSlots = () => {
  const slots = [];
  for (let h = CAL_START_HOUR; h < CAL_END_HOUR; h++) {
    for (let m = 0; m < 60; m += 15) {
      slots.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }
  return slots;
};
export let TIME_SLOTS = generateTimeSlots();
export const setCalDisplayWindow = (start, end) => {
  const s = (start === null || start === undefined || start === "") ? 8 : Number(start);
  CAL_START_HOUR = (Number.isInteger(s) && s >= 0 && s <= 23) ? s : 8;
  const e = (end === null || end === undefined || end === "") ? 20 : Number(end);
  CAL_END_HOUR = (Number.isInteger(e) && e > CAL_START_HOUR && e <= 24) ? e : 20;
  if (CAL_END_HOUR <= CAL_START_HOUR) { CAL_START_HOUR = 8; CAL_END_HOUR = 20; }
  TIME_SLOTS = generateTimeSlots();
};

export const today = new Date();
// STALE_TODAY_RELOAD_v1 (18.07.) -- `today` wird nur EINMAL beim Seiten-Load berechnet; blieb
// der Tab ueber Nacht offen, zeigten die Kalender-Pills "Heute"/"Morgen" weiter den Lade-Tag
// (Meldung Torsten + Natalie, Screenshot 18.07.: App dachte "Heute"=Do 16.07. am Sa 18.07.).
// Fix: bei Datumswechsel EIN sauberer Reload (bringt zugleich frische Termindaten) -- aber nur,
// wenn der Tab unsichtbar ist oder gerade Sichtbarkeit/Fokus zurueckbekommt, damit niemandem
// mitten in der Arbeit (offenes Modal, halb ausgefuelltes Formular) die Seite weggezogen wird.
(() => {
  const _todayKey = today.toDateString();
  const _dateRolled = () => new Date().toDateString() !== _todayKey;
  const _onReturn = () => {
    try { if (document.visibilityState === "visible" && _dateRolled()) window.location.reload(); } catch (e) {}
  };
  document.addEventListener("visibilitychange", _onReturn);
  window.addEventListener("focus", _onReturn);
  // Verdeckter Tab: still im Hintergrund neu laden, dann ist beim Zurueckkommen alles frisch
  setInterval(() => {
    try { if (document.hidden && _dateRolled()) window.location.reload(); } catch (e) {}
  }, 5 * 60 * 1000);
})();
export const formatDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
// BIWEEKLY_SHIFT_v1 -- 14-Tage-Rhythmus: Wochen-Parität eines Datums (0/1).
// Fortlaufender Wochenindex seit Montag 1970-01-05 (UTC-Rechnung, DST-sicher) —
// EXAKT dieselbe Formel wie services/booking.week_parity_of auf dem Server.
export const weekParityOf = (ds) => {
  const [y, m, d] = String(ds).slice(0, 10).split("-").map(Number);
  const days = Math.floor((Date.UTC(y, m - 1, d) - Date.UTC(1970, 0, 5)) / 86400000);
  return ((Math.floor(days / 7) % 2) + 2) % 2;
};
// Gilt diese Schicht-Zeile an diesem Datum? (week_parity NULL/undefined = jede Woche)
export const shiftAppliesOn = (s, ds) => s.week_parity == null || Number(s.week_parity) === weekParityOf(ds);
// DATEFMT_SHORT_v1 -- ISO-Datum (YYYY-MM-DD) oder Date -> deutsches Kurzformat tt.mm.jj (z.B. 2026-06-26 -> 26.06.26). Faellt unveraendert zurueck, wenn das Muster nicht passt.
export const formatDateShort = (d) => { if (!d) return ""; const s = typeof d === "string" ? d : formatDate(d); const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s); return m ? `${m[3]}.${m[2]}.${m[1].slice(2)}` : s; };

// CALENDAR_KW_HOLIDAYS_v1 -- ISO-8601 Kalenderwoche
export const getISOWeek = (date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};

// CALENDAR_KW_HOLIDAYS_v1 -- Gausssche Osterformel + bundesweite DE-Feiertage
export const _easterSunday = (year) => {
  const a = year % 19, b = Math.floor(year / 100), c = year % 100;
  const d = Math.floor(b / 4), e = b % 4;
  const f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4), k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
};
export const _addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

// NO_RECEIPT_CHECK_v1 (23.09.2026) -- Kontrolle „Termin ohne Bon" fuer Salons mit Shearly Kasse.
// EINE Stelle fuer Dashboard („Offene To-dos") und Online-Vorkasse („Offene Zahlungen").
// Treffer: Termine der letzten <days> Tage OHNE heute (den laufenden Tag zaehlt die Kasse im Reiter
// „Termine" selbst), die laut Kalender stattgefunden haben (bestaetigt, kein Phasen-Kind -- der
// Master traegt den Preis) und zu denen die Kasse KEINEN Bon gemeldet hat (posReceiptNumber leer).
// Bewusst nicht paymentStatus: „Bar erhalten" in Pro setzt bezahlt ohne Bon, also an der TSE
// vorbei -- genau das soll hier auffallen. Aktiv erst ab dem ersten Tag mit einem Kassen-Bon an
// einem Termin; vorher wurde nicht ueber die Kasse kassiert und die Liste waere nur Laerm
// (Stand 22.09.: 2.410 vergangene Termine ohne jede Zahlung). Frei kassierte Bons (ohne Termin
// angetippt) meldet die Kasse nicht an Pro -- deshalb ist das ein Hinweis, kein Fehlerbefund.
export const appointmentsWithoutReceipt = (appointments, days = 7) => {
  const list = appointments || [];
  let since = null;
  for (const a of list) {
    if (a && a.posReceiptNumber && a.date && (!since || a.date < since)) since = a.date;
  }
  if (!since) return { active: false, since: null, items: [] };
  const now = new Date();
  const todayStr = formatDate(now);
  const fromStr = formatDate(_addDays(now, -days));
  const lower = since > fromStr ? since : fromStr;
  const items = list
    .filter(a => a && a.status === "confirmed" && !a.phaseParentId && !a.posReceiptNumber
      && a.date >= lower && a.date < todayStr)
    .sort((a, b) => (b.date + (b.startTime || "")).localeCompare(a.date + (a.startTime || "")));
  return { active: true, since, items };
};
export const _holidayCache = {};
export const getGermanHolidays = (year) => {
  if (_holidayCache[year]) return _holidayCache[year];
  const easter = _easterSunday(year);
  const map = {};
  const add = (d, name) => { map[formatDate(d)] = name; };
  add(new Date(year, 0, 1), "Neujahr");
  add(_addDays(easter, -2), "Karfreitag");
  add(easter, "Ostersonntag");
  add(_addDays(easter, 1), "Ostermontag");
  add(new Date(year, 4, 1), "Tag der Arbeit");
  add(_addDays(easter, 39), "Christi Himmelfahrt");
  add(_addDays(easter, 49), "Pfingstsonntag");
  add(_addDays(easter, 50), "Pfingstmontag");
  add(new Date(year, 9, 3), "Tag der Deutschen Einheit");
  add(new Date(year, 11, 25), "1. Weihnachtsfeiertag");
  add(new Date(year, 11, 26), "2. Weihnachtsfeiertag");
  _holidayCache[year] = map;
  return map;
};
export const getHolidayName = (date) => {
  const h = getGermanHolidays(date.getFullYear());
  return h[formatDate(date)] || null;
};

// APT_BADGES_v1 + CONFLICT_VISUAL_v1 + CAL_LUXURY_v1 -- Termin-Status-Badges
export const getApptBadges = (apt, customer) => {
  const badges = [];
  // CONFLICT_VISUAL_v2: Warn-Badge nur bei ECHTER Doppelbuchung (zwei beschäftigte Termine
  // verschiedener Kunden überlappen) — NICHT wenn ein Termin sauber in einer fremden Einwirkzeit liegt.
  if (apt && apt._realConflict) {
    badges.push({ key: "conflict", label: "⚠", color: "#F59E0B", title: "Doppelbuchung — bitte prüfen" });
  }
  // Quelle: online vs. KI vs. manuell
  const src = (apt && apt.source) || "manual";
  if (src === "online") badges.push({ key: "online", label: "Online", color: "#3B82F6", title: "Online-Buchung" });
  else if (src === "ai") badges.push({ key: "ai", label: "KI", color: "#7C3AED", title: "KI-Telefonbuchung" });
  // Neukunde: 0 oder 1 Besuche (1 weil dieser Termin schon zaehlt)
  if (customer && (customer.visits === 0 || customer.visits === undefined || customer.visits === null)) {
    badges.push({ key: "new", label: "Neu", color: "#10B981", title: "Neukunde – erste Buchung" });
  }
  // No-Show-Risiko: >= 3 No-Shows
  const noShows = customer ? (customer.noShows ?? customer.no_shows ?? 0) : 0;
  if (noShows >= 3) {
    badges.push({ key: "noshow", label: "⚠ " + noShows, color: "#DC2626", title: noShows + " No-Shows – Vorkasse oder Bestätigung empfohlen" });
  }
  // CUST_NOTE_BADGE_v1 -- Symbol wenn der Kunde eine echte Notiz hat (Hover zeigt den Notiztext),
  // damit das Team im Kalender sofort sieht "zu dem Kunden gibt's eine Notiz".
  if (customer && customer.notes && String(customer.notes).trim()) {
    badges.push({ key: "note", label: "📝", color: "#0EA5E9", title: "Notiz: " + String(customer.notes).trim().slice(0, 140) });
  }
  return badges;
};

// ============================================================
// UTILITY FUNCTIONS
// ============================================================

// SVC_MODIFIER_CRASH_FIX_v1 -- Frisch angelegte Services liegen im Browser-State OHNE
// priceModifiers/durationModifiers (addService). Der direkte Zugriff crashte das Buchungs-
// modal — aber nur bei Wochenend-Datum (isWeekend-Zweig) bzw. Haar-Modifikatoren, daher
// "samstags geht nicht, werktags schon". Fehlender/leerer Aufschlag = neutral (×1).
export const calcDynamicPrice = (service, employee, isWeekend, modifiers = {}) => {
  const pm = service.priceModifiers || {};
  let price = service.basePrice;
  if (isWeekend) price *= (pm.weekend || 1);
  if (employee?.role?.includes("Senior")) price *= (pm.seniorStylist || 1);
  return Math.round(price * 100) / 100;
};

export const calcDynamicDuration = (service, modifiers = {}) => {
  const dm = service.durationModifiers || {};
  let duration = service.baseDuration;
  if (modifiers.longHair) duration *= (dm.longHair || 1);
  if (modifiers.thickHair) duration *= (dm.thickHair || 1);
  return Math.ceil(duration / 5) * 5; // round to 5 min
};

export const getEndTime = (startTime, durationMinutes) => {
  // BUFFER_DEFAULT_OFF_v1 — vorher: hardcoded +5 Min Puffer. Owner kann Puffer
  // pro Salon im Profil aktivieren (default jetzt 0).
  const [h, m] = startTime.split(":").map(Number);
  const totalMin = h * 60 + m + durationMinutes;
  return `${String(Math.floor(totalMin / 60)).padStart(2, "0")}:${String(totalMin % 60).padStart(2, "0")}`;
};

export const timeToMinutes = (t) => {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
};

// ============================================================
// CUST_DUP_HINT_v1 + APPT_NEARBY_HINT_v1 (Toby 20.09.2026, Zusage an Tamara / Salon 24)
// Beides sind reine HINWEISE im Frontend, keine Sperren: Mutter und Tochter mit derselben
// Nummer müssen weiter anlegbar sein. Es wird nur gelesen (geladene Kunden-/Terminliste).
// ============================================================
const _dupFoldName = (s) => String(s || "").toLowerCase()
  .replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").replace(/ß/g, "ss")
  .normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9 ]/g, " ").replace(/\s+/g, " ").trim();
// Letzte 9 Ziffern: deckt +49 151…, 0151…, Leerzeichen und Schrägstriche ab (gleiche Regel wie DSGVO_PURGE_v1)
const _dupPhoneKey = (s) => { const d = String(s || "").replace(/\D/g, ""); return d.length >= 7 ? d.slice(-9) : ""; };

export const findDuplicateCustomers = (customers, { name, phone }, max = 3) => {
  const nKey = _dupFoldName(name);
  const pKey = _dupPhoneKey(phone);
  const nameOk = nKey.length >= 5 && nKey.includes(" "); // erst ab Vor- UND Nachname, sonst Fehlalarme beim Tippen
  if (!pKey && !nameOk) return [];
  const hits = [];
  (customers || []).forEach(c => {
    if (!c) return;
    const samePhone = !!pKey && _dupPhoneKey(c.phone) === pKey;
    const sameName = nameOk && _dupFoldName(c.name) === nKey;
    if (samePhone || sameName) hits.push({ customer: c, samePhone, sameName });
  });
  hits.sort((a, b) => (Number(b.samePhone) + Number(b.sameName)) - (Number(a.samePhone) + Number(a.sameName)));
  return hits.slice(0, max);
};

// Termine desselben Kunden am gewählten Tag oder in den 7 Tagen davor/danach (ohne Storno/No-Show/Blocker)
export const findNearbyAppointments = (appointments, customerId, dateStr, days = 7, max = 3) => {
  if (!customerId || !/^\d{4}-\d{2}-\d{2}$/.test(String(dateStr || ""))) return [];
  const toDay = (s) => { const [y, m, d] = String(s).slice(0, 10).split("-").map(Number); return Math.round(Date.UTC(y, m - 1, d) / 86400000); };
  const base = toDay(dateStr);
  return (appointments || [])
    .filter(a => a && String(a.customerId) === String(customerId) && a.date
      && !["cancelled", "no_show", "noshow"].includes(a.status)
      && !["blocker", "vacation_auto", "phase", "assistant"].includes(a.source)
      && Math.abs(toDay(a.date) - base) <= days)
    .map(a => ({ ...a, _diff: toDay(a.date) - base }))
    .sort((a, b) => Math.abs(a._diff) - Math.abs(b._diff) || String(a.startTime).localeCompare(String(b.startTime)))
    .slice(0, max);
};
